import React, { useEffect, useMemo, useState } from 'react';
import { SquareIcon, PauseIcon, ActivityIcon, UsersIcon, ListChecksIcon, AlertTriangleIcon, Loader2Icon, CheckCircle2Icon, CircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
interface TimedItem {
  appearsAt: number;
  content: string;
  detail?: string;
  status?: 'active' | 'idle' | 'weak';
}
interface TopicItem {
  content: string;
  coveredAt: number | null; // null = never covered during this recording
}
const PARTICIPANTS: TimedItem[] = [{
  appearsAt: 6,
  content: 'Mike — Foreman',
  detail: 'speaking most',
  status: 'active'
}, {
  appearsAt: 14,
  content: 'Dan — Crane Operator',
  detail: 'confirmed lift sequence',
  status: 'active'
}, {
  appearsAt: 28,
  content: 'Luis — Rigger',
  detail: 'discussed rigging and tag lines',
  status: 'active'
}, {
  appearsAt: 42,
  content: 'Jake — Ironworker',
  detail: 'discussed beam positioning',
  status: 'active'
}, {
  appearsAt: 55,
  content: 'Carlos — Laborer',
  detail: 'no clear verbal input yet',
  status: 'weak'
}];
const ALL_TOPICS: TopicItem[] = [{
  content: 'Wind conditions',
  coveredAt: null
}, {
  content: 'Dropped object exposure below lift area',
  coveredAt: null
}, {
  content: 'Emergency stop / communication failure',
  coveredAt: null
}, {
  content: 'PPE confirmation for full crew',
  coveredAt: null
}, {
  content: 'Crane setup and outriggers',
  coveredAt: 18
}, {
  content: 'Rigging inspection',
  coveredAt: 32
}, {
  content: 'Lift path to second floor',
  coveredAt: 44
}, {
  content: 'Exclusion zone',
  coveredAt: 52
}, {
  content: 'Signal person assignment',
  coveredAt: 62
}];
const AI_NOTE_APPEARS_AT = 70;
export function Screen12_Recording({
  onNext
}: ScreenProps) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  const visibleParticipants = useMemo(() => PARTICIPANTS.filter(p => seconds >= p.appearsAt), [seconds]);
  // Sort topics: uncovered first, then covered — covered items sink to bottom
  const sortedTopics = useMemo(() => {
    return [...ALL_TOPICS].sort((a, b) => {
      const aCovered = a.coveredAt !== null && seconds >= a.coveredAt;
      const bCovered = b.coveredAt !== null && seconds >= b.coveredAt;
      if (aCovered === bCovered) return 0;
      return aCovered ? 1 : -1;
    });
  }, [seconds]);
  const coveredCount = useMemo(() => ALL_TOPICS.filter(t => t.coveredAt !== null && seconds >= t.coveredAt).length, [seconds]);
  const showAINote = seconds >= AI_NOTE_APPEARS_AT;
  const analysisStarted = seconds >= 3;
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 8,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1
    }
  };
  return <div className="flex flex-col h-full" data-id="element-455">
      <div className="mb-6 flex flex-col items-center text-center" data-id="element-456">
        <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-457">
          <motion.div animate={{
          opacity: [1, 0.3, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1.5
        }} className="w-2 h-2 rounded-full bg-red-500" data-id="element-458" />
          Recording Live
        </div>
        <h2 className="text-2xl font-bold text-white mb-3" data-id="element-459">
          Recording PTP Meeting
        </h2>
        <div className="flex items-center gap-4 mb-3" data-id="element-460">
          <div className="flex items-center justify-center gap-[3px] h-8 w-16" data-id="element-461">
            {Array.from({
            length: 12
          }).map((_, i) => <motion.div key={i} animate={{
            height: ['20%', `${Math.random() * 80 + 20}%`, '20%']
          }} transition={{
            repeat: Infinity,
            duration: 0.4 + Math.random() * 0.4,
            ease: 'easeInOut'
          }} className="w-[3px] bg-orange-500 rounded-full" data-id="element-462" />)}
          </div>
          <div className="text-4xl font-mono font-light text-white tracking-tight tabular-nums" data-id="element-463">
            {formatTime(seconds)}
          </div>
          <div className="flex items-center justify-center gap-[3px] h-8 w-16" data-id="element-464">
            {Array.from({
            length: 12
          }).map((_, i) => <motion.div key={i} animate={{
            height: ['20%', `${Math.random() * 80 + 20}%`, '20%']
          }} transition={{
            repeat: Infinity,
            duration: 0.4 + Math.random() * 0.4,
            ease: 'easeInOut'
          }} className="w-[3px] bg-orange-500 rounded-full" data-id="element-465" />)}
          </div>
        </div>
        <p className="text-slate-400 text-sm" data-id="element-466">
          AI is analyzing the meeting while it is being recorded.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-467">
        {/* Live Meeting Analysis Header */}
        <div className="flex items-center gap-2 mb-2" data-id="element-468">
          <ActivityIcon className="w-5 h-5 text-orange-500" data-id="element-469" />
          <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-470">
            Live Meeting Analysis
          </h3>
          {!analysisStarted && <motion.div animate={{
          rotate: 360
        }} transition={{
          repeat: Infinity,
          duration: 1,
          ease: 'linear'
        }} data-id="element-471">
              <Loader2Icon className="w-4 h-4 text-slate-500" data-id="element-472" />
            </motion.div>}
        </div>

        {/* Pre-analysis state */}
        {!analysisStarted && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="bg-slate-900/50 border border-slate-800/50 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center" data-id="element-473">
            <Loader2Icon className="w-8 h-8 text-slate-600 mb-3 animate-spin" data-id="element-474" />
            <p className="text-slate-500 text-sm font-medium" data-id="element-475">Listening...</p>
            <p className="text-slate-600 text-xs mt-1" data-id="element-476">
              AI analysis will appear as the meeting progresses
            </p>
          </motion.div>}

        {/* Live AI Note */}
        <AnimatePresence data-id="element-477">
          {showAINote && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3" data-id="element-478">
              <p className="text-orange-300 text-sm leading-snug" data-id="element-479">
                <span className="font-bold" data-id="element-480">AI Note:</span> Meeting is active.
                Coverage is incomplete. Continue discussing hazards, controls,
                and crew responsibilities.
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Participants */}
        {analysisStarted && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-481">
            <div className="flex items-center gap-2 mb-3" data-id="element-482">
              <UsersIcon className="w-4 h-4 text-slate-400" data-id="element-483" />
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider" data-id="element-484">
                Detected Participants
              </h4>
              {visibleParticipants.length < PARTICIPANTS.length && <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1.2,
            ease: 'linear'
          }} className="ml-auto" data-id="element-485">
                  <Loader2Icon className="w-3 h-3 text-slate-600" data-id="element-486" />
                </motion.div>}
            </div>
            {visibleParticipants.length === 0 ? <p className="text-slate-600 text-xs italic" data-id="element-487">
                Detecting speakers...
              </p> : <ul className="space-y-2 text-sm" data-id="element-488">
                <AnimatePresence data-id="element-489">
                  {visibleParticipants.map(p => <motion.li key={p.content} variants={itemVariants} initial="hidden" animate="visible" transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} className={p.status === 'weak' ? 'text-slate-500' : 'text-slate-300'} data-id="element-490">
                      <span className={p.status === 'weak' ? 'text-slate-400 font-medium' : 'text-white font-medium'} data-id="element-491">
                        {p.content}
                      </span>
                      {' — '}
                      {p.detail}
                    </motion.li>)}
                </AnimatePresence>
              </ul>}
          </motion.div>}

        {/* Unified Topic Coverage List */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-492">
          <div className="flex items-center justify-between mb-3" data-id="element-493">
            <div className="flex items-center gap-2" data-id="element-494">
              <ListChecksIcon className="w-4 h-4 text-slate-400" data-id="element-495" />
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider" data-id="element-496">
                Topic Coverage
              </h4>
            </div>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-497">
              {coveredCount}/{ALL_TOPICS.length}
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-slate-800 rounded-full h-1.5 mb-4" data-id="element-498">
            <motion.div className="bg-green-500 h-1.5 rounded-full" animate={{
            width: `${coveredCount / ALL_TOPICS.length * 100}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-499" />
          </div>

          <ul className="space-y-2" data-id="element-500">
            <AnimatePresence data-id="element-501">
              {sortedTopics.map(topic => {
              const isCovered = topic.coveredAt !== null && seconds >= topic.coveredAt;
              return <motion.li key={topic.content} layout transition={{
                layout: {
                  duration: 0.4,
                  ease: 'easeInOut'
                }
              }} className={`flex items-center gap-3 py-1.5 px-2 rounded-lg text-sm transition-colors ${isCovered ? 'bg-green-500/5' : 'bg-amber-500/5'}`} data-id="element-502">
                    {isCovered ? <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15
                }} data-id="element-503">
                        <CheckCircle2Icon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-504" />
                      </motion.div> : <CircleIcon className="w-4 h-4 text-amber-500/50 flex-shrink-0" data-id="element-505" />}
                    <span className={isCovered ? 'text-green-300/70 line-through decoration-green-500/30' : 'text-amber-200'} data-id="element-506">
                      {topic.content}
                    </span>
                    {!isCovered && <span className="ml-auto text-[9px] font-bold text-amber-500/60 uppercase tracking-wider flex-shrink-0" data-id="element-507">
                        Missing
                      </span>}
                  </motion.li>;
            })}
            </AnimatePresence>
          </ul>
        </div>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-508">
        <PrimaryButton onClick={onNext} className="bg-red-500 hover:bg-red-600 shadow-red-500/20" icon={<SquareIcon className="w-5 h-5 fill-current" data-id="element-510" />} data-id="element-509">
          Stop Recording
        </PrimaryButton>
        <SecondaryButton onClick={() => {}} icon={<PauseIcon className="w-5 h-5 fill-current" data-id="element-512" />} data-id="element-511">
          Pause
        </SecondaryButton>
      </div>
    </div>;
}